import { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import * as Api from "@/api";
import CountryFlag from "@/app/domain/callflow/timetables/holidaysets/calendar/CountryFlag";
import EditorForm from "@/design/components/EditorForm";
import SaveIndicator from "@/design/components/SaveIndicator";
import InputAutocomplete from "@/design/forms/InputAutocomplete";
import InputSelect from "@/design/forms/InputSelect";
import InputText from "@/design/forms/InputText";
import CountryCodes from "@/resources/countrycode";
import { number, string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";

import { Stack } from "@mui/material";
import PropTypes from "prop-types";

import prefixReducer from "./prefixReducer";

function OptionForm({ outboundRule, prefix, outboundDispatch, setActivePrefix }) {
  const phoneNumberList = useSelector((state) => state.domain.pbx.phoneNumbers);
  const inboundRouteStore = useSelector((state) => state.domain.inboundRoutes);

  const [type, setType] = useState(null);

  const outgoingNumberList = useMemo(
    () =>
      phoneNumberList?.map((phoneNumber) => ({
        did: phoneNumber.did,
        name: inboundRouteStore.list.find((inbound) => inbound.did === phoneNumber.did)?.name || "",
      })),
    [inboundRouteStore.list, phoneNumberList],
  );

  // Form
  const { value, dispatch, setSaveState, formState, errors, allErrors, save } = useSave({
    initialValue: prefix,
    reducer: prefixReducer,
    validator: () => ({
      destinationPrefix: string()
        .required({ message: "Prefix is required." })
        .length([1, 12], { message: "Prefix must be at most 12 characters." })
        .truthy(
          outboundRule.destinationPrefixes.filter(
            (_prefix) => _prefix.destinationPrefix === value.destinationPrefix && _prefix.id !== value.id,
          ).length === 0,
          { message: "There already is another rule with this prefix." },
        ),
      callerId: number().required({ message: "Number is required." }),
    }),
    onSubmit: () => Api.storeOutboundDestination(outboundRule, value),
    onSuccess: (response) => {
      if (!value.id) {
        dispatch({ type: "id", value: response.data.destinationPrefix.id });
        outboundDispatch({ type: "addPrefix", prefix: { ...value, id: response.data.destinationPrefix.id } });
        setActivePrefix({ ...value, id: response.data.destinationPrefix.id });
      } else {
        outboundDispatch({ type: "updatePrefix", prefix: value });
      }
    },
  });

  const country = useMemo(
    () => CountryCodes.find((_country) => value.destinationPrefix.startsWith(_country.phone)) || {},
    [value.destinationPrefix],
  );

  const selectedType = type ?? (prefix.id && country.phone !== value.destinationPrefix ? "Custom" : "Country");

  const formHandler = (property, value) => {
    dispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  return (
    <EditorForm
      title="Prefix settings"
      action={<SaveIndicator formState={formState} errors={allErrors} save={save} mini />}
    >
      <InputSelect
        id="type"
        label="Type"
        onChange={(e, value) => setType(value)}
        value={selectedType}
        selectValue="value"
        selects={[{ value: "Country" }, { value: "Custom" }]}
        getOptionLabel={(option) => option.value}
        error={errors}
      />
      {selectedType === "Custom" ? (
        <InputText
          id={`destinationPrefix`}
          label="Prefix"
          name="destinationPrefix"
          onChange={formHandler}
          value={value.destinationPrefix}
          error={errors}
        />
      ) : (
        <InputAutocomplete
          id={`destinationPrefix`}
          label="Prefix"
          name="destinationPrefix"
          onChange={formHandler}
          value={value.destinationPrefix}
          selectValue="phone"
          selects={CountryCodes}
          getOptionLabel={(option) => `${option.phone} \u00b7 ${option.name}`}
          renderOptionLabel={(option) => (
            <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
              <CountryFlag code={option.code} />
              <span>{`${option.phone} \u00b7 ${option.name}`}</span>
            </Stack>
          )}
          error={errors}
        />
      )}
      <InputAutocomplete
        id="callerId"
        label="Caller ID"
        onChange={formHandler}
        value={value.callerId}
        selectValue="did"
        selects={outgoingNumberList}
        getOptionLabel={(option) => (option.name ? `${option.did} \u00b7 ${option.name}` : option.did)}
        renderOptionLabel={(option) => (
          <>
            {option.did}
            {option.name !== "" && <span style={{ opacity: 0.6 }}>&nbsp;&middot; {option.name}</span>}
          </>
        )}
        error={errors}
      />
    </EditorForm>
  );
}

OptionForm.propTypes = {
  outboundRule: PropTypes.object,
  prefix: PropTypes.object,
  outboundDispatch: PropTypes.func,
  setActivePrefix: PropTypes.func,
};

export default OptionForm;
